import { useEffect, useRef, type ReactNode } from 'react';

interface ModalProps {
  /** Names the dialog for assistive technology, and titles the panel variant. */
  title: string;
  /**
   * Called for every way out — Escape, the backdrop, the close button. The modal expects
   * to be unmounted in response: it has no closed state of its own to fall back to.
   */
  onClose: () => void;
  /**
   * `panel` sits in the middle of the screen with a heading, for something you read or
   * fill in. `sheet` rises from the bottom edge with no chrome at all, for a short list
   * of choices that has to stay in thumb reach on a phone.
   */
  variant?: 'panel' | 'sheet';
  children: ReactNode;
}

/**
 * The app's only overlay.
 *
 * A native `<dialog>` opened with `showModal()`, rather than a div with a high z-index:
 * the top layer, the backdrop, focus containment, restoring focus to whatever opened it
 * and dismissal on Escape are all browser behaviour that way. Reimplementing any of that
 * is how overlays end up unusable with a keyboard or a screen reader.
 *
 * Deliberately not used for *interrupting* anything on the shopping path — an item editor
 * is inline, and adding something takes no confirmation. This is for the occasional admin
 * that would otherwise clutter the list.
 *
 * The two uses on that path are not really exceptions, because each of them *is* the
 * answer to the tap that opened it rather than something in its way: the photo viewer,
 * where an image wants the top layer so the list's scroll container cannot clip it, and
 * the sheet that asks where a photo should come from.
 */
export function Modal({ title, onClose, variant = 'panel', children }: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;

    // Guarded rather than closed on cleanup: StrictMode runs this twice in development,
    // and showModal() on an already-open dialog throws. Unmounting the element takes it
    // out of the top layer on its own, so there is nothing to undo.
    if (dialog && !dialog.open) {
      dialog.showModal();
    }
  }, []);

  return (
    <dialog
      ref={ref}
      className={variant === 'sheet' ? 'modal modal--sheet' : 'modal'}
      aria-label={title}
      // Fires for Escape as well as for close(), so every dismissal arrives here.
      onClose={onClose}
      // A click whose target is the dialog element itself landed on the backdrop: the
      // content is inside .modal__body, so anything within it targets that instead.
      onClick={(event) => {
        if (event.target === ref.current) {
          onClose();
        }
      }}
    >
      <div className="modal__body">
        {variant === 'panel' && (
          <header className="modal__header">
            <h2 className="modal__title">{title}</h2>

            <button type="button" className="icon-button" onClick={onClose} aria-label="Close">
              <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
                <path
                  d="M6 6l12 12M18 6L6 18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </header>
        )}

        {children}
      </div>
    </dialog>
  );
}
